import { Dropdown, Menu, MenuButton, Stack, useTheme } from '@mui/joy';
import { GridApi } from 'ag-grid-community';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MANAGE_FILTERS_EXPIRATION_TIME } from 'core/constants';
import { Loader } from 'core/elements';
import { LocalStorageKeys } from 'core/enums';
import { useGetCustomFilters, useLocalStorage } from 'core/hooks';
import { AgGridDataRow, ICustomFilter } from 'core/types';
import { getAgGridFilterConfig, objectToJson } from 'core/utils';
import { Color, Size, Variant } from 'theme/enums';

import { ManageFilters } from './ManageFilters';
import { UpsertFilterButton } from './UpsertFilterButton';

interface IProps {
  entity: string;
  gridApi: GridApi<AgGridDataRow>;
}

export const ManageFiltersButton = ({ entity, gridApi }: IProps) => {
  const [selectedFilter, setSelectedFilter] = useState<ICustomFilter>();

  const theme = useTheme();
  const { t } = useTranslation();
  const { saveObject, getObject } = useLocalStorage();

  const selectedFilterKey = `${LocalStorageKeys.SELECTED_FILTER_PREFIX}_${entity}`;

  const getSelectedFilterFromStorage = useCallback(() => {
    return getObject<ICustomFilter>(selectedFilterKey);
  }, [selectedFilterKey, getObject]);

  const saveSelectedFilterToStorage = useCallback(
    (filter: ICustomFilter) => {
      saveObject(selectedFilterKey, filter, MANAGE_FILTERS_EXPIRATION_TIME);
    },
    [selectedFilterKey, saveObject]
  );

  const handleSetSelectedFilter = useCallback(
    (filter: ICustomFilter) => {
      setSelectedFilter(filter);
      saveSelectedFilterToStorage(filter);
    },
    [saveSelectedFilterToStorage]
  );

  const { records, isLoading } = useGetCustomFilters({
    filters: {
      entity: `"${entity}"`,
    },
  });

  useEffect(() => {
    if (records?.length && !selectedFilter) {
      let filterToSelect = getSelectedFilterFromStorage();

      if (!filterToSelect) {
        filterToSelect = records.find((filter) => filter.default);
      }

      if (filterToSelect) {
        setSelectedFilter(filterToSelect);
      }
    }
  }, [records, selectedFilter, getSelectedFilterFromStorage]);

  if (!gridApi) {
    return null;
  }

  const currentConfig = objectToJson(getAgGridFilterConfig(gridApi));

  return (
    <Dropdown>
      <MenuButton color={Color.NEUTRAL} variant={Variant.OUTLINED}>
        {t('buttonLabelManageFilters')}
      </MenuButton>

      <Menu
        keepMounted
        placement="bottom-end"
        size={Size.SM}
        sx={{ width: '400px', zIndex: 'var(--joy-zIndex-modal)' }}
      >
        {isLoading && <Loader />}
        {!isLoading && (
          <Stack>
            <Stack sx={{ maxHeight: '350px', overflowY: 'auto' }}>
              <ManageFilters
                key={entity}
                gridApi={gridApi}
                filters={records}
                selectedFilter={selectedFilter}
                setSelectedFilter={handleSetSelectedFilter}
                currentConfig={currentConfig}
                entity={entity}
              />
            </Stack>
            <Stack
              sx={{
                paddingY: 1,
                paddingX: 2,
                borderTop: `1px solid ${theme.palette.neutral[200]}`,
              }}
            >
              <UpsertFilterButton
                entity={entity}
                currentConfig={currentConfig}
                onSubmitSuccess={handleSetSelectedFilter}
              />
            </Stack>
          </Stack>
        )}
      </Menu>
    </Dropdown>
  );
};
